// Phase 1A Phase 9B.4C — useHomeActivity hook.
//
// Read-only consumer of four existing event sources surfaced through
// per-user RLS:
//   - agent_logs_for_user (view; user-scoped via cycle ownership)
//   - rg_audit_events     (table; user-scoped row-level)
//   - cashout_events      (table; user-scoped row-level)
//   - rollover_compounding_decisions (table; user-scoped row-level)
//
// Trust contract:
//   - No writes. No RPCs. No realtime subscriptions. No timers. No
//     polling. Fetch once on mount + a manual refetch().
//   - Four parallel reads with small limits (default 5 each). If one
//     source errors, the merged result still surfaces the successful
//     sources and the hook reports a non-blocking error string.
//   - Ordering / truncation / forbidden-copy contract live entirely in
//     the pure helper home-activity-snapshot.ts (covered by Vitest).

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../../auth/useAuthSession.ts";
import { getBrowserClient } from "../../supabase/browser.ts";
import {
  agentLogsToItems,
  auditEventsToItems,
  cashoutEventsToItems,
  compoundingDecisionsToItems,
  HOME_ACTIVITY_DEFAULT_MAX,
  mergeActivityItems,
  type AgentLogRow,
  type CashoutRow,
  type DecisionRow,
  type HomeActivityItem,
  type RgAuditRow,
} from "./home-activity-snapshot.ts";

export interface UseHomeActivityState {
  items: HomeActivityItem[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useHomeActivity(
  maxItems: number = HOME_ACTIVITY_DEFAULT_MAX,
): UseHomeActivityState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const [items, setItems] = useState<HomeActivityItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    if (!user) {
      setItems([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    const sourceLimit = HOME_ACTIVITY_DEFAULT_MAX;
    const [agentRes, auditRes, cashoutRes, decisionRes] = await Promise.all([
      supabase
        .from("agent_logs_for_user")
        .select("id, agent_name, created_at, cycle_id")
        .order("created_at", { ascending: false, nullsFirst: false })
        .limit(sourceLimit),
      supabase
        .from("rg_audit_events")
        .select("id, created_at, event_type")
        .order("created_at", { ascending: false })
        .limit(sourceLimit),
      supabase
        .from("cashout_events")
        .select("id, created_at, cycle_id, cashout_value")
        .order("created_at", { ascending: false })
        .limit(sourceLimit),
      supabase
        .from("rollover_compounding_decisions")
        .select("id, created_at, cycle_id")
        .order("created_at", { ascending: false })
        .limit(sourceLimit),
    ]);

    const errs: string[] = [];
    const agentItems = agentRes.error
      ? (errs.push(`agent_logs: ${agentRes.error.message}`), [])
      : agentLogsToItems((agentRes.data ?? []) as AgentLogRow[]);
    const auditItems = auditRes.error
      ? (errs.push(`audit_events: ${auditRes.error.message}`), [])
      : auditEventsToItems((auditRes.data ?? []) as RgAuditRow[]);
    const cashoutItems = cashoutRes.error
      ? (errs.push(`cashout_events: ${cashoutRes.error.message}`), [])
      : cashoutEventsToItems((cashoutRes.data ?? []) as CashoutRow[]);
    const decisionItems = decisionRes.error
      ? (errs.push(`compounding_decisions: ${decisionRes.error.message}`), [])
      : compoundingDecisionsToItems((decisionRes.data ?? []) as DecisionRow[]);

    const merged = mergeActivityItems(
      [agentItems, auditItems, cashoutItems, decisionItems],
      maxItems,
    );
    setItems(merged);
    setError(errs.length === 0 ? null : errs.join("; "));
    setLoading(false);
  }, [supabase, user, maxItems]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return useMemo<UseHomeActivityState>(
    () => ({ items, loading, error, refetch }),
    [items, loading, error, refetch],
  );
}
